import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Paper, Typography } from '@mui/material';
import {
  startConversation,
  continueChat,
  addUserMessage,
  addErrorMessage,
  clearFollowupQuestions,
} from './chatbotSlice';

const MAX_CONVERSATION_MESSAGES = 20; // Maximum number of user messages per conversation

const ChatBubbleIcon = () => (
  <svg
    width="60%"
    height="60%"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M20.9996 11.5C20.9996 16.1944 17.194 20 12.4996 20C11.4228 20 10.3928 19.7998 9.44478 19.4345C9.27145 19.3678 9.18478 19.3344 9.11586 19.3185C9.04807 19.3029 8.999 19.2963 8.92949 19.2937C8.85881 19.291 8.78127 19.299 8.62619 19.315L3.50517 19.8444C3.01692 19.8948 2.7728 19.9201 2.6288 19.8322C2.50337 19.7557 2.41794 19.6279 2.3952 19.4828C2.36909 19.3161 2.48575 19.1002 2.71906 18.6684L4.35472 15.6408C4.48942 15.3915 4.55677 15.2668 4.58728 15.1469C4.6174 15.0286 4.62469 14.9432 4.61505 14.8214C4.60529 14.6981 4.55119 14.5376 4.443 14.2166C4.15547 13.3636 3.99962 12.45 3.99962 11.5C3.99962 6.80558 7.8052 3 12.4996 3C17.194 3 20.9996 6.80558 20.9996 11.5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const QuestionPromptCard = ({ 
  prompt, 
  onInputChange,
  padding = '16px 20px',
  textColor = '#303030',
  backgroundColor = '#fff',
  clearFollowupOnClick = false,
  queryType = ''
}) => {
  const dispatch = useDispatch();
  const { currentConversationId, currentDatabaseChatId, isGenieConversation } = useSelector((state) => state.chatbot);
  
  // Calculate conversation limit
  const messages = useSelector((state) => state.chatbot.messages);
  const userMessageCount = messages.filter(msg => msg.sender === 'user').length;
  const isConversationLimitReached = userMessageCount >= MAX_CONVERSATION_MESSAGES;

  const handleClick = () => {
    // Check conversation limit
    if (isConversationLimitReached) {
      dispatch(addErrorMessage({ 
        text: `Conversation limit reached. You can send up to ${MAX_CONVERSATION_MESSAGES} messages per conversation. Please start a new chat.` 
      }));
      return;
    }

    // Clear followup questions if this is a followup question
    if (clearFollowupOnClick) {
      dispatch(clearFollowupQuestions());
    }
    
    if (onInputChange) {
      onInputChange(prompt);
    }
    
    setTimeout(() => {
      const questionText = prompt;
      if (!questionText || !questionText.trim()) return;
      dispatch(addUserMessage({ text: questionText }));
      if (onInputChange) {
        onInputChange('');
      }
      (async () => {
        try {
          if (!currentConversationId) {
            const resultAction = await dispatch(startConversation({ question: questionText, queryType: queryType }));
            if (startConversation.rejected.match(resultAction)) {
              dispatch(addErrorMessage({ text: `Sorry, I encountered an error: ${resultAction.payload || 'Unknown error'}` }));
            }
          } else {
            const resultAction = await dispatch(continueChat({ 
              conversationId: currentConversationId, 
              question: questionText,
              chatId: currentDatabaseChatId, // Send ChatID if user is in the same chat
              queryType: queryType,
              isGenieConversation: isGenieConversation // Pass flag to API
            }));
            if (continueChat.rejected.match(resultAction)) {
              dispatch(addErrorMessage({ text: `Sorry, I encountered an error: ${resultAction.payload || 'Unknown error'}` }));
            }
          }
        } catch (err) {
          console.error('Error sending message:', err);
          dispatch(addErrorMessage({ text: `Sorry, I encountered an error: ${err.message || 'Unknown error'}` }));
        }
      })();
    }, 100);
  };

  return (
    <Paper
      className="prompt-card"
      onClick={handleClick}
      sx={{
        padding: padding,
        cursor: 'pointer',
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        transition: 'all 0.2s',
        backgroundColor: backgroundColor,
        '&:hover': {
          borderColor: '#007DC3',
          backgroundColor: '#EBF5FF',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0, 125, 195, 0.15)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: '40px',
            height: '40px',
            minWidth: '40px',
            background: 'linear-gradient(135deg, #2d89fb 0%, #09b2e0 100%)',
            borderRadius: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1.5,
            mt: 0.2,
            flexShrink: 0,
            color: '#ffffff'
          }}
        >
          <ChatBubbleIcon />
        </Box>
        <Typography variant="body2" sx={{ fontSize: '14px', lineHeight: 1.6, color: textColor }}>
          {prompt}
        </Typography>
      </Box>
    </Paper>
  );
};

export default QuestionPromptCard;

